import React from 'react';
import { oraIta } from '../utils/oraItaliana';
import '../styles/SchedaFisiologica.css';
import '../styles/SchedaFisiologicaConsole.css';

/**
 * StatoSalvataggio — la conferma, una per riquadro, di un'anamnesi che si
 * salva da sola (14 ottobre 2026, richiesta di Alex: «non e' chiaro se una
 * riga nuova degli interventi si salva senza premere Aggiungi»).
 *
 * UN COMPONENTE SOLO per tutti i riquadri che si salvano da soli (parto,
 * famiglia, lavoro, interventi, gravidanze, abitudini, modifica di una voce o
 * di una terapia): se ognuno scrivesse il suo, fra sei mesi uno direbbe
 * «salvato» e l'altro «ok». Dice, in quest'ordine di importanza:
 *
 *   «Non salvato»        rosso, NON sparisce da solo, si clicca per riprovare
 *                        (come i segni accanto ai campi). Vince su tutto: se
 *                        un campo del riquadro non e' passato, il riquadro non
 *                        puo' dire «salvato»;
 *   «Sto salvando…»      mentre una scrittura va;
 *   «Salvato ✓ 14:32»    l'ora italiana dell'ultimo salvataggio riuscito, e
 *                        RESTA: la spunta accanto al campo sbiadisce dopo tre
 *                        secondi, ma chi torna al riquadro dopo un minuto
 *                        deve poter leggere se quello che ha scritto e' passato.
 *
 * Prima dello stato, la frase fissa «Si salva appena esci dal campo»: e' la
 * regola, e va detta dove si scrive. Chi ha una frase sua piu' lunga la passa
 * come `frase`, che deve contenere quella.
 *
 * Lo stato lo calcola dai campi del riquadro (`campi`), cosi' un riquadro non
 * dice «salvato» per quello che e' successo in un altro: `stato` e
 * `salvatoAlle` sono quelli di `useSalvaCampo`.
 *
 * @param {string[]}  campi         i campi che sono di questo riquadro
 * @param {Object}    stato         campo -> 'in_corso' | 'salvato' | 'non_salvato'
 * @param {Object}    salvatoAlle   campo -> istante (ms) dell'ultimo salvataggio
 * @param {Function}  onRiprova     riprova i campi rossi del riquadro
 * @param {boolean}   bloccato      c'e' un valore che non puo' partire (una data
 *                                  scritta male): il riquadro non e' salvato, e il
 *                                  perche' lo dice chi lo usa, vicino ai campi
 * @param {string}    frase         sostituisce la frase fissa
 */
const FRASE = 'Si salva appena esci dal campo.';

const StatoSalvataggio = ({ campi, stato = {}, salvatoAlle = {}, onRiprova = null, bloccato = false, frase = FRASE }) => {
    const rossi = campi.filter((c) => stato[c] === 'non_salvato');
    const inCorso = campi.some((c) => stato[c] === 'in_corso');
    const ultimo = campi.reduce((max, c) => Math.max(max, salvatoAlle[c] || 0), 0);

    let segno = null;
    if (rossi.length || bloccato) {
        segno = (
            <button type="button" className="sf-stato-no" onClick={onRiprova || undefined}
                    title={bloccato && !rossi.length ? 'Correggi quello che è in rosso: finché non è corretto non si salva'
                        : 'Non è passato: clicca per riprovare'}>
                Non salvato — riprova
            </button>
        );
    } else if (inCorso) {
        segno = <span className="sf-stato-corso" role="status">Sto salvando…</span>;
    } else if (ultimo) {
        segno = <span className="sf-stato-ok" role="status">Salvato ✓ {oraIta(ultimo)}</span>;
    }

    return (
        <div className="sf-stato">
            <span className="sf-aiuto sf-stato-frase">{frase}</span>
            {segno}
        </div>
    );
};

/**
 * La frase di un modulo di riga NUOVA (voce d'anamnesi, terapia, annotazione
 * sulla mappa), 14 ottobre 2026: crea una riga firmata, quindi resta un
 * pulsante (regola di CLAUDE.md, «un gesto che crea una riga nuova resta un
 * pulsante»), ma il testo scritto non si perde in silenzio: il modulo e'
 * protetto come gli altri (domanda al cambio di schermata, avviso del browser)
 * e questa frase dice la differenza con le voci in modifica, che si salvano
 * da sole.
 */
export const FraseRigaNuova = ({ pulsante }) => (
    <span className="sf-aiuto sf-frase-nuova">
        Una riga nuova si salva solo col pulsante «{pulsante}»: finché non lo premi non è in cartella.
    </span>
);

export default StatoSalvataggio;
